import React, { useMemo } from 'react';
import throttle from 'lodash.throttle';

import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import Iconify from 'src/components/iconify';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import ScrollbarJs from 'src/components/scrollbar';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Scrollbar = ScrollbarJs as React.ComponentType<any>;
import { FILTERS_CONFIG } from 'src/models/recipe/list/filters-config';
import {
  MultiselectSearchFilterConfig,
  MultiselectFilterConfig,
  SelectFilterConfig,
  type FilterConfig,
} from 'src/models/recipe/list/filter-types';

import Select from './select';
import Multiselect from './multiselect';
import MultiselectWithSearch from './multiselect-with-search';

// ----------------------------------------------------------------------
// Register UI components on config instances (once, at module load time)

FILTERS_CONFIG.forEach((config) => {
  if (config instanceof MultiselectSearchFilterConfig) config.registerComponent(MultiselectWithSearch);
  else if (config instanceof MultiselectFilterConfig) config.registerComponent(Multiselect);
  else if (config instanceof SelectFilterConfig) config.registerComponent(Select);
});

// ----------------------------------------------------------------------

type FilterItem = {
  config: FilterConfig;
  value: unknown;
  options: { code: string; label: string }[];
};

type Props = {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  items: FilterItem[];
  onFilters: (name: string, value: unknown) => void;
  handleBucketSearch: (name: string, value: string) => void;
  canReset: boolean;
  onResetFilters: () => void;
};

export default function RecipeFilters({
  open,
  onOpen,
  onClose,
  items,
  onFilters,
  handleBucketSearch,
  canReset,
  onResetFilters,
}: Props) {
  const throttledBucketSearch = useMemo(
    () => throttle((name: string, value: string) => handleBucketSearch(name, value), 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 2, pr: 1, pl: 2.5 }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Filtry
      </Typography>

      <Tooltip title="Reset">
        <IconButton onClick={onResetFilters}>
          <Badge color="error" variant="dot" invisible={!canReset}>
            <Iconify icon="solar:restart-bold" />
          </Badge>
        </IconButton>
      </Tooltip>

      <IconButton onClick={onClose}>
        <Iconify icon="mingcute:close-line" />
      </IconButton>
    </Stack>
  );

  return (
    <>
      <Button
        disableRipple
        color="inherit"
        endIcon={
          <Badge color="error" variant="dot" invisible={!canReset}>
            <Iconify icon="ic:round-filter-list" />
          </Badge>
        }
        onClick={onOpen}
      >
        Filtry
      </Button>

      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 280 } }}
      >
        {renderHead}

        <Divider />

        <Scrollbar sx={{ px: 2.5, py: 3 }}>
          <Stack spacing={3}>
            {items
              .filter(({ config }) => config.component !== null)
              .map(({ config, value, options }) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const Component = config.component as React.ComponentType<any>;
                const extraProps =
                  config instanceof MultiselectSearchFilterConfig
                    ? {
                        onInputChange: (_event: unknown, newValue: string) =>
                          throttledBucketSearch(config.code, newValue),
                      }
                    : {};

                return (
                  <Component
                    key={config.code}
                    filterCode={config.code}
                    filterName={config.label}
                    options={options}
                    value={value ?? config.defaultValue}
                    onChange={(_event: unknown, newValue: unknown) =>
                      onFilters(config.code, newValue)
                    }
                    {...extraProps}
                  />
                );
              })}
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
}



